import { WmToggletip } from "@watermarkinsights/ripple-react";
import { useIntl } from "react-intl";
import translate from "@/i18n/translate";
import { useReducer } from "react";
import "./Lightcast.scss";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Doughnut } from "react-chartjs-2";
import {
  WmTabItem,
  WmTabList,
  WmTabPanel
} from "@watermarkinsights/ripple-react";
import WidgetLoader from "../common/elements/WidgetLoader";
import ApiError from "../common/elements/ApiError";
import { LaborForceBreakdown, Tab } from "./Interface";

interface LaborForceProps {
  laborForceBreakdown: LaborForceBreakdown | null;
  laborForceBreakdownLoader: Boolean;
  lfbApiError: Boolean;
}

interface LaborForceState {
  selectedTab: string;
}

interface LaborForceAction {
  type: string;
  data: any;
}

ChartJS.register(ArcElement, Tooltip, Legend);
const externalTooltipHandler = function (context: any) {
  // Tooltip Element
  let tooltipEl = document.getElementById("chartjs-tooltip");

  // Create element on first render
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<table></table>";
    document.querySelector(".doughnut-chart")?.appendChild(tooltipEl);
  }

  document.addEventListener("click", function handleClickOutsideBox(event) {
    if (tooltipEl) {
      if (!tooltipEl.contains(event.target as Node | null)) {
        const tooltip = context.tooltip;
        if (tooltip.getActiveElements().length > 0) {
          tooltip.setActiveElements([], { x: 0, y: 0 });
        }
      }
    }
  });
  // Hide if no tooltip
  const tooltipModel = context.tooltip;
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = "" + 0;
    return;
  }

  // Set caret Position
  tooltipEl.classList.remove("above", "below", "no-transform");
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign);
  } else {
    tooltipEl.classList.add("no-transform");
  }

  // Set Text
  if (tooltipModel.body) {
    createTooltipHtml(tooltipModel, tooltipEl);
  }

  // Display, position, and set styles for font
  tooltipEl.style.opacity = "" + 1;
  tooltipEl.style.position = "absolute";
  tooltipEl.style.left = tooltipModel.caretX + "px";
  tooltipEl.style.top = tooltipModel.caretY + "px";
  tooltipEl.style.padding =
    tooltipModel.padding + "px " + tooltipModel.padding + "px";
  tooltipEl.style.pointerEvents = "none";
};

function createTooltipHtml(tooltipModel: any, tooltipEl: HTMLElement | null) {
  const datapoints = tooltipModel.dataPoints[0];

  let innerHtml = "<thead>";

  innerHtml += "</thead><tbody>";

  let style = "background:" + "#FFF";

  style += "; border-width: 2px";
  style += "; width: 168px";
  style += "; box-shadow: 0px 5px 12px 1px rgba(57, 57, 57, 0.5)";
  style += "; padding: 16px";
  const div =
    '<div style="' +
    style +
    '">' +
    '<div style ="margin-bottom:4px; font-weight:600;" >' +
    datapoints.formattedValue +
    "</div>" +
    `<div> ${datapoints.dataset.labels[datapoints.dataIndex]}  </div>` +
    "</div>";
  innerHtml += "<tr><td>" + div + "</td></tr>";

  innerHtml += "</tbody>";

  let tableRoot = tooltipEl && tooltipEl.querySelector("table");
  if (tableRoot) {
    tableRoot.innerHTML = innerHtml;
  }
}

let options = {
  plugins: {
    legend: {
      display: false
    },
    events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],
    tooltip: {
      events: ["click"],
      enabled: false,
      position: "nearest",
      external: externalTooltipHandler
    }
  },
  maintainAspectRatio: true,
  devicePixelRatio: 2,
  responsive: true,
  animation: false
};

export default function LaborForce({
  laborForceBreakdown,
  laborForceBreakdownLoader,
  lfbApiError
}: LaborForceProps) {
  const tooltipDescription = useIntl().formatMessage({
    id: "app.lightcast.laborForce.tooltipDescription"
  });

  const tooltipLabel = useIntl().formatMessage({
    id: "app.lightcast.laborForce.tooltipLabel"
  });

  const analyticView = useIntl().formatMessage({
    id: "app.lighcast.laborForce.analyticView"
  });

  const tableView = useIntl().formatMessage({
    id: "app.lighcast.laborForce.tableView"
  });

  const reducer = (state: LaborForceState, action: LaborForceAction) => {
    switch (action.type) {
      case "SetSelectedTab":
        return { ...state, selectedTab: action.data };
      default:
        return state;
    }
  };

  const tabOption = [
    {
      name: analyticView,
      id: "analytic-view"
    },
    {
      name: tableView,
      id: "table-view"
    }
  ];

  const initialState = {
    selectedTab: "labor-force-tab-1"
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const onWmTabSelected = (event: any) => {
    dispatch({ type: "SetSelectedTab", data: event.detail.tabId });
  };

  function TableView() {
    return (
      <div className="org-table labor-force-table">
        <table className="data-table -shadow">
          <thead className="dataheader">
            <tr className="dataheaderrow">
              <th className="dataheadercell">
                {translate("app.lightcast.laborForce.categoryLabel")}
              </th>
              <th className="dataheadercell">
                {translate("app.lightcast.laborForce.definitionLabel")}
              </th>
              <th className="dataheadercell">
                {translate("app.lightcast.laborForce.populationLabel")}
              </th>
              <th className="dataheadercell flex-row">
                {translate("app.lightcast.laborForce.percentageLabel")}
                <WmToggletip
                  className="percent-tooltip"
                  label={tooltipLabel}
                  tooltip={tooltipDescription}
                  tooltipPosition="top-left"
                ></WmToggletip>
              </th>
            </tr>
          </thead>
          <tbody className="databody">
            <tr className="datarow">
              <td className="datacell">
                <div>
                  {translate("app.lightcast.laborForce.totalPopulation")}
                </div>
              </td>
              <td className="datacell">
                {translate(
                  "app.lightcast.laborForce.totalPopulationDefinition"
                )}
              </td>
              <td className="datacell">
                {laborForceBreakdown.totalPopulation}
              </td>
              <td className="datacell">100%</td>
            </tr>
            <tr className="datarow">
              <td className="datacell">
                <div className="level-1">
                  {translate("app.lightcast.laborForce.civillianPopulation")}
                </div>
              </td>
              <td className="datacell">
                {translate(
                  "app.lightcast.laborForce.civillianPopulationDefinition"
                )}
              </td>
              <td className="datacell">
                {laborForceBreakdown.civilianPopulation}
              </td>
              <td className="datacell">
                {laborForceBreakdown.civilianPopulationPercentage}%
              </td>
            </tr>
            <tr className="datarow">
              <td className="datacell">
                <div className="level-2">
                  {translate("app.lightcast.laborForce.notInLaborForce")}
                </div>
              </td>
              <td className="datacell">
                {translate(
                  "app.lightcast.laborForce.notInLaborForceDefinition"
                )}
              </td>
              <td className="datacell">
                {laborForceBreakdown.notInLaborForce}
              </td>
              <td className="datacell">
                {laborForceBreakdown.notInLaborForcePercentage}%
              </td>
            </tr>
            <tr className="datarow">
              <td className="datacell">
                <div className="level-2">
                  {translate("app.lightcast.laborForce.label")}
                </div>
              </td>
              <td className="datacell">
                {translate("app.lightcast.laborForce.definition")}
              </td>
              <td className="datacell">
                {laborForceBreakdown.laborForcePopulation}
              </td>
              <td className="datacell">
                {laborForceBreakdown.laborForcePopulationPercentage}%
              </td>
            </tr>
            <tr className="datarow">
              <td className="datacell">
                <div className="level-3">
                  {translate("app.lightcast.laborForce.employed")}
                </div>
              </td>
              <td className="datacell">
                {translate("app.lightcast.laborForce.employedDefinition")}
              </td>
              <td className="datacell">{laborForceBreakdown.employed}</td>
              <td className="datacell">
                {laborForceBreakdown.employedPercentage}%
              </td>
            </tr>
            <tr className="datarow">
              <td className="datacell">
                <div className="level-3">
                  {translate("app.lighcast.laborForce.unemployed")}
                </div>
              </td>
              <td className="datacell">
                {translate("app.lighcast.laborForce.unemployedDefinition")}
              </td>
              <td className="datacell">{laborForceBreakdown.unemployed}</td>
              <td className="datacell">
                {laborForceBreakdown.unemployedPercentage}%
              </td>
            </tr>
            <tr className="datarow">
              <td className="datacell">
                <div className="level-1">
                  {translate("app.lighcast.laborForce.militaryPopulation")}
                </div>
              </td>
              <td className="datacell">
                {translate(
                  "app.lighcast.laborForce.militaryPopulationDefinition"
                )}
              </td>
              <td className="datacell">{laborForceBreakdown.under16}</td>
              <td className="datacell">
                {laborForceBreakdown.under16Percentage}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="paper-card labor-force-conatiner">
      <div className="header">
        <div className="info" id="labor-force-info">
          <h3 className="title">
            {translate("app.lightcast.laborForce.headingLabel")}
          </h3>
          <div className="description -labor-des">
            {translate("app.lightcast.laborForce.helperLabel")}
          </div>
        </div>
      </div>

      {!lfbApiError ? (
        <div className="body" id="labor-force-tab-component">
          {!laborForceBreakdownLoader && laborForceBreakdown && (
            <>
              <div className="tabbar">
                <WmTabList
                  id="tab-with-labor-force-breakdown"
                  selectedTab={state.selectedTab}
                  onWmTabSelected={onWmTabSelected}
                >
                  {tabOption.map((tab: Tab, index: number) => (
                    <WmTabItem
                      tabId={"labor-force-tab-" + (index + 1)}
                      key={tab.id + (index + 1)}
                    >
                      {tab.name}
                    </WmTabItem>
                  ))}
                </WmTabList>
              </div>
              <WmTabPanel
                className="tab-panel"
                id="analytic-view"
                tabId={"labor-force-tab-1"}
              >
                <div className="analytic-view-container">
                  <AnalyticView laborForceBreakdown={laborForceBreakdown} />
                  <AnalyticViewTable
                    laborForceBreakdown={laborForceBreakdown}
                  />
                </div>
              </WmTabPanel>
              <WmTabPanel
                className="tab-panel"
                id="table-view"
                tabId={"labor-force-tab-2"}
              >
                <TableView />
              </WmTabPanel>
            </>
          )}

          {laborForceBreakdownLoader && (
            <WidgetLoader
              loaderContainerClasses="loader"
              loaderClasses="content"
            />
          )}
          {!laborForceBreakdownLoader && !laborForceBreakdown && <ApiError />}
        </div>
      ) : (
        <ApiError />
      )}
    </div>
  );
}

interface AnalyticViewProps {
  laborForceBreakdown: LaborForceBreakdown;
}

export function AnalyticView({ laborForceBreakdown }: AnalyticViewProps) {
  function calculatePercentage(value: number, totalValue: number) {
    return (value / totalValue) * 100;
  }

  function getCircumfrence(value: number, totalValue: number) {
    return Number((value * totalValue) / 100);
  }

  const civilianPercentage = useIntl().formatMessage(
    { id: "app.lightcast.laborForce.civillianPopulationPercentage" },
    {
      percentage: laborForceBreakdown.civilianPopulationPercentage
    }
  );
  const militaryPercentage = useIntl().formatMessage(
    { id: "app.lighcast.laborForce.militaryPopulationPercentage" },
    {
      percentage: laborForceBreakdown.under16Percentage
    }
  );
  const laborForcePercentage = useIntl().formatMessage(
    { id: "app.lightcast.laborForcePercentage" },
    {
      percentage: laborForceBreakdown.laborForcePopulationPercentage
    }
  );
  const notInLaborForcePercentage = useIntl().formatMessage(
    { id: "app.lightcast.laborForce.notInLaborForcePercentage" },
    {
      percentage: laborForceBreakdown.notInLaborForcePercentage
    }
  );
  const employedPercentage = useIntl().formatMessage(
    { id: "app.lightcast.laborForce.employedPercentage" },
    {
      percentage: laborForceBreakdown.employedPercentage
    }
  );
  const laborForceBreakdownChart = useIntl().formatMessage({
    id: "app.lighcast.laborForce.laborForceBreakdownChart"
  });
  const LaborBreakdownChartAccessibilityDescription = useIntl().formatMessage({
    id: "app.lighcast.laborForce.chartAccessibilityDescription"
  });
  const unemployedPercentage = useIntl().formatMessage(
    { id: "app.lightcast.laborForce.unemployedPercentage" },
    {
      percentage: laborForceBreakdown.unemployedPercentage
    }
  );

  let laborForceCivilian = Number(
    laborForceBreakdown.civilianPopulation.split(",").join("")
  );
  let laborForceUnder16 = Number(
    laborForceBreakdown.under16.split(",").join("")
  );

  let totalPopulation = Number(
    laborForceBreakdown.totalPopulation.split(",").join("")
  );

  let laborForceCivilianPercetage = calculatePercentage(
    laborForceCivilian,
    totalPopulation
  );

  let circumfrenceSecondLevel = getCircumfrence(
    laborForceCivilianPercetage,
    360
  );

  let laborForce = Number(
    laborForceBreakdown.laborForcePopulation.split(",").join("")
  );

  let laborForcePercent = calculatePercentage(laborForce, laborForceCivilian);

  let notInLaborForce = Number(
    laborForceBreakdown.notInLaborForce.split(",").join("")
  );
  let notInLaborForcePercent = calculatePercentage(
    notInLaborForce,
    laborForceCivilian
  );

  let thirdLevelCircumfrence = getCircumfrence(
    laborForcePercent,
    circumfrenceSecondLevel
  );
  let thirdLevelRotation =
    (notInLaborForcePercent * circumfrenceSecondLevel) / 100;

  const data = {
    datasets: [
      {
        labels: [employedPercentage, unemployedPercentage],
        data: [
          Number(laborForceBreakdown.employed.split(",").join("")),
          Number(laborForceBreakdown.unemployed.split(",").join(""))
        ],
        backgroundColor: ["#ff5f4e", "#59211b"],
        hoverBackgroundColor: ["#ff5f4e", "#59211b"],
        hoverBorderWidth: 0,
        circumference: thirdLevelCircumfrence,
        rotation: thirdLevelRotation,
        borderWidth: 2
      },
      {
        labels: [notInLaborForcePercentage, laborForcePercentage],
        data: [
          Number(laborForceBreakdown.notInLaborForce.split(",").join("")),
          Number(laborForceBreakdown.laborForcePopulation.split(",").join(""))
        ],
        backgroundColor: ["#8b86ca", "#2e1b46"],
        hoverBackgroundColor: ["#8b86ca", "#2e1b46"],
        hoverBorderWidth: 0,
        circumference: circumfrenceSecondLevel,
        borderWidth: 2
      },
      {
        labels: [civilianPercentage, militaryPercentage],
        data: [laborForceCivilian, laborForceUnder16],
        backgroundColor: ["#19a1a9", "#0b484c"],
        hoverBackgroundColor: ["#19a1a9", "#0b484c"],
        hoverBorderWidth: 0,
        circumference: 360,
        borderWidth: 2
      }
    ]
  };
  return (
    <div
      className="doughnut-chart"
      tabIndex={0}
      aria-label={LaborBreakdownChartAccessibilityDescription}
      id="lfb-chart"
    >
      <div className="doughnut-chart-info">
        <div className="total-population-value">
          {laborForceBreakdown.totalPopulation}
        </div>
        <div className="total-population-label">
          {translate("app.lightcast.laborForce.totalPopulation")}
        </div>
      </div>
      <Doughnut data={data} options={options} aria-label={laborForceBreakdownChart} />
    </div>
  );
}
interface AnalyticViewTableProps {
  laborForceBreakdown: LaborForceBreakdown;
  forHtml?: boolean;
}
export function AnalyticViewTable({
  laborForceBreakdown,
  forHtml
}: AnalyticViewTableProps) {
  return (
    <div className="hierarchy-info instituition-context-desc">
      <div tabIndex={0}>
        <div className="total-population">
          <div className="total-population-row">
            {translate("app.lightcast.laborForce.totalPopulation")}
          </div>
          <div>{laborForceBreakdown.totalPopulation}</div>
          {forHtml && <span className="labor-force-percent">100%</span>}
        </div>
      </div>
      <div className="margin-top" tabIndex={0}>
        <div className="civialian-population">
          <div className="civilian-label">
            <span className="legend-civilian"></span>
            {translate("app.lightcast.laborForce.civillianPopulation")}
          </div>
          <div>{laborForceBreakdown.civilianPopulation}</div>
          {forHtml && (
            <span className="labor-force-percent">
              {laborForceBreakdown.civilianPopulationPercentage}%
            </span>
          )}
        </div>
      </div>
      <div tabIndex={0} className="margin-top first-level">
        <div className="not-in-labor-force">
          <div className="not-in-labor-force-label">
            <span className="legend-not-in-labor-force"></span>
            {translate("app.lightcast.laborForce.notInLaborForce")}
          </div>
          <div className="population">
            {laborForceBreakdown.notInLaborForce}
          </div>
          {forHtml && (
            <span className="labor-force-percent">
              {laborForceBreakdown.notInLaborForcePercentage}%
            </span>
          )}
        </div>
      </div>
      <div className="margin-top first-level" tabIndex={0}>
        <div className="labor-force">
          <div className="labor-force-label">
            <span className="legend-labor-force"></span>
            {translate("app.lightcast.laborForce.label")}
          </div>
          <div className="population">
            {laborForceBreakdown.laborForcePopulation}
          </div>
          {forHtml && (
            <span className="labor-force-percent">
              {laborForceBreakdown.laborForcePopulationPercentage}%
            </span>
          )}
        </div>
      </div>
      <div className="second-level margin-top" tabIndex={0}>
        <div className="employed">
          <div className="employed-label">
            <span className="legend-employed"></span>
            {translate("app.lightcast.laborForce.employed")}
          </div>
          <div className="population">{laborForceBreakdown.employed}</div>
          {forHtml && (
            <span className="labor-force-percent">
              {laborForceBreakdown.employedPercentage}%
            </span>
          )}
        </div>
      </div>
      <div className="margin-top second-level" tabIndex={0}>
        <div className="unemployed">
          <div className="unemployed-label">
            <span className="legend-unemployed"></span>
            {translate("app.lighcast.laborForce.unemployed")}
          </div>
          <div className="population">{laborForceBreakdown.unemployed}</div>
          {forHtml && (
            <span className="labor-force-percent">
              {laborForceBreakdown.unemployedPercentage}%
            </span>
          )}
        </div>
      </div>
      <div className="margin-top" tabIndex={0}>
        <div className="military">
          <div className="under16-label">
            <span className="legend-military"></span>
            {translate("app.lighcast.laborForce.militaryPopulation")}
          </div>
          <div className="population">{laborForceBreakdown.under16}</div>
          {forHtml && (
            <span className="labor-force-percent">
              {laborForceBreakdown.under16Percentage}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
